import React, { Component } from 'react';
import { number, bool } from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { createSelector } from 'reselect';

import { ROUTES } from 'constants/routes';
import { message } from 'utils/stringUtils';
import { retrieveDigitalBillAdherenceStatus } from 'utils/accountUtils';
import { UNSUBSCRIBED, PENDING, SUBSCRIBED, SUBSCRIBED_FROM_UTILITY } from 'constants/userForms';
import { appConfig } from 'config/customization';
import { accountPropTypes, actionCreators as accountActions } from 'redux/accountHandlers';
import ServiceAdherenceDetail from 'app/components/ServiceAdherenceDetail';
import { DIGITAL_BILL, AUTOMATIC_DEBIT } from 'constants/common';

import AccountDetail from './layout';

const adhrenceMessageMap = {
  [UNSUBSCRIBED]: message('AccountStatus:notAdheredService'),
  [PENDING]: message('AccountDetails:adherenceInProgress'),
  [SUBSCRIBED]: message('AccountDetails:subscribed'),
  [SUBSCRIBED_FROM_UTILITY]: message('AccountDetails:subscribed')
};

class AccountDetailMobileContainer extends Component {
  state = {
    deleteModalIsOpen: false
  };

  componentDidMount() {
    this.handleRedirection();
  }

  componentDidUpdate() {
    this.handleRedirection();
  }

  handleRedirection = () => {
    if (!this.shouldRender()) {
      this.props.dispatch(push(ROUTES.ACCOUNTS));
    }
  };

  AutomaticDebitButton = () =>
    this.props.account.adherence_to_automatic_debit === UNSUBSCRIBED && (
      <ServiceAdherenceDetail
        adherence={this.props.account.adherence_to_automatic_debit}
        formId={this.props.automaticDebitFormId}
        service={AUTOMATIC_DEBIT}
        account={this.props.account}
      />
    );

  DigitalBillButton = () =>
    retrieveDigitalBillAdherenceStatus(this.props.account) === UNSUBSCRIBED && (
      <ServiceAdherenceDetail
        adherence={retrieveDigitalBillAdherenceStatus(this.props.account)}
        formId={this.props.digitalBillFormId}
        service={DIGITAL_BILL}
        account={this.props.account}
      />
    );

  shouldRender = () => !!this.props.account;

  handleDeleteAccount = async accountId => {
    await this.props.dispatch(accountActions.deleteAccount(accountId));
    this.setState({ deleteModalIsOpen: false });
  };

  handleDeleteModalOpen = () => {
    this.setState({ deleteModalIsOpen: true });
  };

  handleDeleteModalClose = () => {
    this.setState({ deleteModalIsOpen: false });
  };

  formatAccount = () =>
    this.props.account &&
    [
      {
        title: message('Accounts:clientNumber'),
        subtitle: this.props.account.client_number,
        show: true
      },
      {
        title: message('Accounts:holderName'),
        subtitle: this.props.account.holder_name,
        show: true
      },
      {
        title: message('Accounts:address'),
        subtitle: this.props.account.address,
        show: true
      },
      {
        title: message('ClientSummary:rate'),
        subtitle: this.props.account.rate,
        show: true
      },
      {
        title: `${message('Accounts:accountRelationship')}:`,
        subtitle: this.props.account.main
          ? message('Accounts:mainCustomer')
          : message('Accounts:secondaryCustomer'),
        show: appConfig.showUserAccountRelationship
      },
      {
        title: message('AccountDetails:digitalBill'),
        subtitle: adhrenceMessageMap[retrieveDigitalBillAdherenceStatus(this.props.account)],
        rightButton: this.DigitalBillButton(),
        show:
          appConfig.digitalBillShortcuts &&
          adhrenceMessageMap[retrieveDigitalBillAdherenceStatus(this.props.account)]
      },
      {
        title: message('AccountDetails:automaticDebit'),
        subtitle: adhrenceMessageMap[this.props.account.adherence_to_automatic_debit],
        rightButton: this.AutomaticDebitButton(),
        show:
          appConfig.automaticDebitShortcuts &&
          adhrenceMessageMap[this.props.account.adherence_to_automatic_debit]
      }
    ].filter(field => field.show);

  render() {
    const { account, deleteAccountLoading } = this.props;

    const formattedAccount = this.formatAccount();

    return formattedAccount ? (
      <AccountDetail
        formattedAccount={formattedAccount}
        deleteAccountLoading={deleteAccountLoading}
        deleteModalIsOpen={this.state.deleteModalIsOpen}
        deleteAccountId={account.id}
        onDeleteAccount={this.handleDeleteAccount}
        onDeleteModalOpen={this.handleDeleteModalOpen}
        onDeleteModalClose={this.handleDeleteModalClose}
      />
    ) : (
      <div />
    );
  }
}

AccountDetailMobileContainer.propTypes = {
  account: accountPropTypes,
  digitalBillFormId: number,
  automaticDebitFormId: number,
  deleteAccountLoading: bool
};

const accountSelector = createSelector(
  (store, props) => props.location.params,
  store => store.account.accounts,
  (id, accounts) => id && accounts.find(el => el.id === id)
);

const mapStateToProps = (store, props) => ({
  account: accountSelector(store, props),
  digitalBillFormId: store.utility.digitalBillForm && store.utility.digitalBillForm.id,
  automaticDebitFormId: store.utility.automaticDebitForm && store.utility.automaticDebitForm.id,
  deleteAccountLoading: store.account.deleteAccountLoading
});

export default connect(mapStateToProps)(AccountDetailMobileContainer);
